import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../Css/Register.css";
import togetherPhoto from "../Assets/JoinUsTogether.jpeg";

/*
 * Deployed Google Apps Script Web App URL — the same script that powers
 * the footer newsletter signup. It now also handles JoinUs submissions,
 * routed to the "joinus" tab via the form:'joinus' parameter below.
 */
const JOINUS_ENDPOINT =
  'https://script.google.com/macros/s/AKfycbw4eY8n8b6IoA8z0Z6Y6UocG9FS3mcbQ3E7361up_ThTPTs4QMA2x4DnG_A8vbooFmA/exec';

const JoinUs = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    city: "",
    state: "",
    country: "",
    role: "",
  });

  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting
  const navigate = useNavigate();

  const validateInput = () => {
    const { role, firstName, lastName, city, state, country, email } = formData;
    const alphaRegex = /^[A-Za-z\s]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (
      !alphaRegex.test(firstName) ||
      !alphaRegex.test(lastName) ||
      !alphaRegex.test(city) ||
      !alphaRegex.test(state) ||
      !alphaRegex.test(country)
    ) {
      setError("Fields (except email) must contain only alphabets and spaces.");
      return false;
    }

    if (!emailRegex.test(email)) {
      setError("Invalid email format.");
      return false;
    }

    if (role === "") {
      setError("Please select a Role.");
      return false;
    }

    setError("");
    return true;
  };

  // Calls the Apps Script Web App over JSONP so the browser can read the
  // response without hitting CORS restrictions (same pattern as the
  // footer newsletter signup).
  const submitJoinUsViaJsonp = (params) =>
    new Promise((resolve, reject) => {
      const callbackName = `sfeJoinUsCb_${Date.now()}_${Math.floor(
        Math.random() * 1000000
      )}`;
      const script = document.createElement("script");
      let settled = false;

      const cleanup = () => {
        delete window[callbackName];
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      };

      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error("timeout"));
      }, 12000);

      window[callbackName] = (data) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        cleanup();
        resolve(data);
      };

      const query = new URLSearchParams({
        ...params,
        callback: callbackName,
      }).toString();

      script.src = `${JOINUS_ENDPOINT}?${query}`;
      script.onerror = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        cleanup();
        reject(new Error("network"));
      };
      document.body.appendChild(script);
    });

  const saveToSheet = async () => {
    setStatus("submitting");
    try {
      const data = await submitJoinUsViaJsonp({
        ...formData,
        form: "joinus",
        source: "joinus-page",
      });

      if (data && data.ok === false) {
        setError("Something went wrong. Please try again.");
        setStatus("idle");
        return;
      }

      alert("Thanks! Your message has been sent.");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        city: "",
        state: "",
        country: "",
        role: "",
      });
      setStatus("idle");
    } catch (err) {
      console.error("Error saving data: ", err);
      alert("An error occurred while saving the form data.");
      setStatus("idle");
    }
  };

  const handleSave = () => {
    if (validateInput()) {
      saveToSheet();
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      city: "",
      state: "",
      country: "",
      role: "",
    });
    setError("");
    navigate("/");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  return (
    <div className="register-page">
      <div className="register-layout">
        <div className="register-why-box">
          <h3 className="register-why-heading">Get in Touch with SportsForEquity</h3>
          <img
            src={togetherPhoto}
            alt="SportsForEquity community coming together"
            className="register-why-photo"
          />
          <div className="register-why-body">
            <p>
              Across the world, non&#8209;communicable diseases like Parkinson's and cancer are rising sharply.
              These conditions affect millions of families — and they disproportionately impact communities
              with limited access to early screening, movement&#8209;based wellness, and long&#8209;term support.
            </p>
            <p>SportsForEquity is committed to advancing health equity by:</p>
            <ul>
              <li>Promoting active aging and neuro wellness through free table&#8209;tennis&#8209;based programs</li>
              <li>Supporting cancer&#8209;care access, including cervical&#8209;cancer screening for underserved women</li>
            </ul>
            <p>
              Your support helps us strengthen both neuro health and cancer&#8209;care equity — two pillars of a healthier future.
            </p>
          </div>
          <div className="register-subtitle">
            Contact: <a href="mailto:info@sportsforequity.live" className="register-contact-inline">info@sportsforequity.live</a>
          </div>
        </div>

        <div className="register-form-col">
          <h3 className="register-form-heading">Send A Message</h3>

          {error && <p className="register-error">{error}</p>}

          <form className="register-form">
            <div className="register-field">
              <label>First Name:</label>
              <input name="firstName" value={formData.firstName} onChange={handleChange} />
            </div>

            <div className="register-field">
              <label>Last Name:</label>
              <input name="lastName" value={formData.lastName} onChange={handleChange} />
            </div>

            <div className="register-field">
              <label>Email Address:</label>
              <input name="email" value={formData.email} onChange={handleChange} />
            </div>

            <div className="register-field">
              <label>City:</label>
              <input name="city" value={formData.city} onChange={handleChange} />
            </div>

            <div className="register-field">
              <label>State:</label>
              <input name="state" value={formData.state} onChange={handleChange} />
            </div>

            <div className="register-field">
              <label>Country:</label>
              <input name="country" value={formData.country} onChange={handleChange} />
            </div>

            <div className="register-field">
              <label>Role:</label>
              <select name="role" value={formData.role} onChange={handleChange}>
                <option value="">Select a Value</option>
                <option value="Team">Team</option>
                <option value="Trainee">Trainee</option>
                <option value="Advisory">Advisory</option>
              </select>
            </div>

            <div className="register-actions">
              <button
                type="button"
                onClick={handleSave}
                className="register-btn"
                disabled={status === "submitting"}
              >
                {status === "submitting" ? "Sending…" : "Send Message"}
              </button>
              <button type="button" onClick={handleCancel} className="register-btn register-btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default JoinUs;